import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { reduzirImagem } from '@/lib/imagem';
import { guardarDestino, pegarDestino } from '@/lib/destino';
import { ehAppEmbutido, nomeDoApp } from '@/lib/navegador';
import FundoEvento from '@/components/feature/FundoEvento';
import GoogleIcon from './components/GoogleIcon';
import EntrarAba from './components/EntrarAba';
import CriarContaAba, { type ResultadoCriar } from './components/CriarContaAba';
import SeusDados, { type DadosPessoais } from './components/SeusDados';

const CHAVE_PREFERENCIA = 'slc_ja_usou';

type Aba = 'entrar' | 'criar';

function buildRedirectTo(destino: string): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/entrar?voltar=${encodeURIComponent(destino)}`;
}

function isEmailJaCadastrado(error: { message?: string; code?: string; status?: number }): boolean {
  const msg = (error.message || '').toLowerCase();
  return error.code === 'user_already_exists' || msg.includes('already registered');
}

export default function EntrarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const destino = searchParams.get('voltar');
  const { user, pronto } = useAuth();
  // Quem chega de um link de inscrição (QR do sábado) é quase sempre novo.
  const veioDeInscricao = !!destino && destino.startsWith('/inscricao/');

  const [checking, setChecking] = useState(true);
  const [aba, setAba] = useState<Aba>('criar');
  const [emailEntrar, setEmailEntrar] = useState('');
  const [avisoEntrar, setAvisoEntrar] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [dadosAbertos, setDadosAbertos] = useState(false);
  const [nomeNovoUsuario, setNomeNovoUsuario] = useState('');

  // Quem já está logado nunca vê o formulário: vai direto para onde queria ir.
  // Só quando sabemos de verdade que não há sessão é que mostramos o login.
  useEffect(() => {
    if (!pronto) return;
    if (user) {
      navigate(pegarDestino() ?? '/', { replace: true });
      return;
    }
    let jaUsou = false;
    try {
      jaUsou = localStorage.getItem(CHAVE_PREFERENCIA) === '1';
    } catch {
      jaUsou = false;
    }
    setAba(veioDeInscricao ? 'criar' : jaUsou ? 'entrar' : 'criar');
    setChecking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronto, user]);

  const marcarPreferencia = () => {
    try {
      localStorage.setItem(CHAVE_PREFERENCIA, '1');
    } catch {
      /* ignora */
    }
  };

  const trocarAba = (nova: Aba) => {
    setAba(nova);
    setAvisoEntrar(null);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setGoogleError(null);
    const voltar = destino ?? '/';
    guardarDestino(voltar);
    const redirectTo = buildRedirectTo(voltar);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) {
      setGoogleLoading(false);
      setGoogleError('Não conseguimos continuar agora. Tente de novo em alguns instantes.');
      return;
    }
    marcarPreferencia();
  };

  const handleEntrar = async (
    email: string,
    senha: string,
  ): Promise<{ mensagem?: string } | null> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
    if (error) {
      return { mensagem: 'E-mail ou senha incorretos.' };
    }
    marcarPreferencia();
    navigate(pegarDestino() ?? '/', { replace: true });
    return null;
  };

  const handleCriar = async (nome: string, email: string, senha: string): Promise<ResultadoCriar> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password: senha,
      options: { data: { full_name: nome } },
    });

    if (error) {
      if (isEmailJaCadastrado(error)) {
        setEmailEntrar(email);
        setAvisoEntrar('Este e-mail já tem conta. Use a aba Entrar.');
        setAba('entrar');
        return { tipo: 'ja-existe' };
      }
      if (error.code === 'weak_password' || /at least 6 characters/i.test(error.message || '')) {
        return { tipo: 'erro', mensagem: 'A senha precisa ter pelo menos 6 caracteres.' };
      }
      return {
        tipo: 'erro',
        mensagem: 'Não conseguimos criar sua conta agora. Tente de novo em alguns instantes.',
      };
    }

    const user = data.user;
    if (user) {
      await supabase.from('usuarios').upsert(
        { id: user.id, nome, email, papel: 'usuario' },
        { onConflict: 'id' },
      );

      const { data: perfil } = await supabase.from('vw_meu_perfil').select('*').maybeSingle();
      if (perfil && !perfil.cadastro_incompleto) {
        navigate(pegarDestino() ?? '/', { replace: true });
        return { tipo: 'ok' };
      }
    }

    marcarPreferencia();
    setNomeNovoUsuario(nome);
    setDadosAbertos(true);
    return { tipo: 'ok' };
  };

  const handleSalvarDados = async (dados: DadosPessoais): Promise<void> => {
    if (!user) {
      navigate(pegarDestino() ?? '/', { replace: true });
      return;
    }

    let fotoUrl: string | null = null;
    if (dados.foto) {
      try {
        const reduzido = await reduzirImagem(dados.foto);
        const caminho = `${user.id}/perfil-${crypto.randomUUID()}.jpg`;
        const { error } = await supabase.storage
          .from('fichas')
          .upload(caminho, reduzido, { contentType: 'image/jpeg' });
        if (!error) fotoUrl = caminho;
      } catch {
        // Foto não subiu: segue sem ela.
      }
    }

    const cpfLimpo = dados.cpf ? dados.cpf.replace(/\D/g, '') : null;
    const telefoneLimpo = dados.telefone ? dados.telefone.replace(/\D/g, '') : null;

    const { error: errParticipante } = await supabase.rpc('salvar_meu_perfil', {
      p_nome: dados.nome,
      p_cpf: cpfLimpo,
      p_nascimento: dados.nascimento || null,
      p_telefone: telefoneLimpo,
      p_email: null,
      p_cep: dados.cep || null,
      p_endereco: dados.endereco || null,
      p_complemento: dados.complemento || null,
      p_bairro: dados.bairro || null,
      p_cidade: dados.cidade || null,
      p_uf: dados.uf || null,
      p_foto_url: fotoUrl,
    });
    if (errParticipante) throw errParticipante;

    navigate(pegarDestino() ?? '/', { replace: true });
  };

  const handlePularDados = () => {
    navigate(pegarDestino() ?? '/', { replace: true });
  };

  if (dadosAbertos) {
    return (
      <SeusDados
        nomeInicial={nomeNovoUsuario}
        onSalvar={handleSalvarDados}
        onPular={handlePularDados}
      />
    );
  }

  if (checking) {
    return (
      <FundoEvento>
        <div className="flex min-h-[100dvh] items-center justify-center px-4 py-8">
          <div className="w-full max-w-[420px] overflow-hidden rounded-3xl bg-white/90 shadow-xl backdrop-blur-md">
            <div className="p-6 sm:p-8">
              <div className="mx-auto h-6 w-40 animate-pulse rounded bg-secondary-100" />
              <div className="mx-auto mt-3 h-4 w-56 animate-pulse rounded bg-secondary-100" />
              <div className="mt-6 h-12 w-full animate-pulse rounded bg-secondary-100" />
              <div className="mt-6 h-10 w-full animate-pulse rounded-full bg-secondary-100" />
              <div className="mt-6 h-12 w-full animate-pulse rounded bg-secondary-100" />
            </div>
          </div>
        </div>
      </FundoEvento>
    );
  }

  return (
    <FundoEvento>
      <div className="flex min-h-[100dvh] items-center justify-center px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-3xl bg-white/90 shadow-xl backdrop-blur-md">
          <div className="p-6 sm:p-8">
            <div className="text-center">
              <span className="text-[22px] font-bold text-primary-600">Se Liga Católico</span>
              <p className="mt-1 text-sm text-foreground-500">
                Entre para se inscrever ou organizar um evento.
              </p>
            </div>

            {ehAppEmbutido ? (
              <div className="mt-6 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-3">
                <p className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-500">
                    <i className="ri-information-line" />
                  </span>
                  O Google não permite login aqui dentro do {nomeDoApp()}. Abra no navegador (botão
                  acima) ou entre com e-mail e senha.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="mt-6 flex h-12 w-full items-center justify-center gap-3 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white text-[15px] font-medium text-foreground-950 transition-colors hover:bg-secondary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <GoogleIcon />
                Continuar com o Google
              </button>
            )}

            {googleError && (
              <p className="mt-2 text-sm text-danger-600" role="alert">
                {googleError}
              </p>
            )}

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-secondary-200" />
              <span className="text-sm text-foreground-500">ou</span>
              <div className="h-px flex-1 bg-secondary-200" />
            </div>

            <div className="flex rounded-full bg-secondary-100 p-1">
              {veioDeInscricao ? (
                <>
                  <button
                    type="button"
                    onClick={() => trocarAba('criar')}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-full py-2 text-sm font-medium transition-colors ${
                      aba === 'criar'
                        ? 'bg-white text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    Criar conta
                  </button>
                  <button
                    type="button"
                    onClick={() => trocarAba('entrar')}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-full py-2 text-sm font-medium transition-colors ${
                      aba === 'entrar'
                        ? 'bg-white text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    Já tenho conta · Entrar
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => trocarAba('entrar')}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-full py-2 text-sm font-medium transition-colors ${
                      aba === 'entrar'
                        ? 'bg-white text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    Entrar
                  </button>
                  <button
                    type="button"
                    onClick={() => trocarAba('criar')}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-full py-2 text-sm font-medium transition-colors ${
                      aba === 'criar'
                        ? 'bg-white text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    Criar conta
                  </button>
                </>
              )}
            </div>

            <div className="mt-6">
              {aba === 'entrar' ? (
                <EntrarAba
                  email={emailEntrar}
                  onEmailChange={setEmailEntrar}
                  aviso={avisoEntrar}
                  onEntrar={handleEntrar}
                />
              ) : (
                <CriarContaAba onCriar={handleCriar} />
              )}
            </div>
          </div>
        </div>
      </div>
    </FundoEvento>
  );
}